import { getApiBase, SystemConfig } from "@/configs";
import { setAuthToken } from "@/lib/auth-cookie";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export type LoginStep = "email" | "code";

const API_BASE = getApiBase();

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export interface UseLoginOptions {
  onSuccess?: () => void;
}

export function useLogin(options: UseLoginOptions = {}) {
  const { onSuccess } = options;
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [codeValue, setCodeValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasVerificationError, setHasVerificationError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileSiteKey = SystemConfig.turnstileSiteKey;
  const sendingRef = useRef(false);
  const [resendUntil, setResendUntil] = useState(0);
  const [resendSeconds, setResendSeconds] = useState(0);

  const startCooldown = useCallback((seconds = 60) => {
    setResendUntil(Date.now() + seconds * 1000);
    setResendSeconds(seconds);
  }, []);

  useEffect(() => {
    if (!resendUntil) return;
    const tick = () => setResendSeconds(Math.max(0, Math.ceil((resendUntil - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [resendUntil]);

  const canSubmitEmail = isValidEmail(email) && !isSending && resendSeconds === 0 && (!turnstileSiteKey || turnstileToken.length > 0);
  const canResend = canSubmitEmail && !isVerifying;

  const reset = useCallback(() => {
    setStep("email");
    setEmail("");
    setCodeValue("");
    setIsSending(false);
    setIsVerifying(false);
    setHasVerificationError(false);
    setErrorMsg("");
    setInfoMsg("");
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
  }, []);

  const requestLogin = useCallback(async () => {
    if (!isValidEmail(email) || sendingRef.current || isVerifying || Date.now() < resendUntil) return;
    if (turnstileSiteKey && !turnstileToken) return;

    sendingRef.current = true;
    setIsSending(true);
    if (step === "code") startCooldown();
    setErrorMsg("");
    setInfoMsg("");

    try {
      const response = await axios.post(`${API_BASE}/account/login/request`, {
        email: email.trim(),
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      startCooldown();
      setCodeValue("");
      setHasVerificationError(false);
      setInfoMsg(
        response.status === 201
          ? "A valid code has already been sent. Please check your inbox."
          : "We have sent a 6-digit confirmation code to your email.",
      );
      setStep("code");
    } catch (err: any) {
      if (err.response?.status === 429) {
        const retryAfter = err.response?.data?.data?.rateLimit?.retryAfter;
        startCooldown(typeof retryAfter === "number" && Number.isFinite(retryAfter) ? Math.max(60, Math.ceil(retryAfter)) : 60);
      }
      const serverMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      setErrorMsg(serverMessage || "Something went wrong. Please try again later.");
      resetTurnstile();
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  }, [email, isVerifying, resendUntil, step, startCooldown, resetTurnstile, turnstileSiteKey, turnstileToken]);

  const verifyCode = useCallback(
    async (codeToVerify?: string) => {
      const targetCode = codeToVerify ?? codeValue;
      const codeNumber = Number(targetCode);
      if (Number.isNaN(codeNumber) || codeNumber < 100000 || codeNumber > 999999 || isVerifying || sendingRef.current) return;

      setIsVerifying(true);
      setHasVerificationError(false);
      setErrorMsg("");

      try {
        const response = await axios.post(`${API_BASE}/account/login/verify`, {
          email: email.trim(),
          code: codeNumber,
        });
        const token = response.data?.data?.token;
        if (!token || typeof token !== "string") {
          setHasVerificationError(true);
          setErrorMsg("Login succeeded but no token was returned.");
          setIsVerifying(false);
          return;
        }

        setAuthToken(token);
        if (onSuccess) onSuccess();
      } catch (err: any) {
        const serverMessage = err.response?.data?.error || err.response?.data?.message || err.message;
        setHasVerificationError(true);
        setErrorMsg(serverMessage || "Invalid code. Please try again.");
        setIsVerifying(false);
      }
    },
    [codeValue, email, isVerifying, onSuccess],
  );

  const handleCodeChange = useCallback(
    (newCode: string) => {
      const next = newCode.replace(/[^0-9A-Za-z]/g, "").slice(0, 6);
      setCodeValue(next);
      if (errorMsg) setErrorMsg("");
      if (next.length === 6 && !hasVerificationError && !isVerifying) {
        verifyCode(next);
      }
    },
    [errorMsg, hasVerificationError, isVerifying, verifyCode],
  );

  const goBackToEmail = useCallback(() => {
    setStep("email");
    setIsVerifying(false);
    setCodeValue("");
    setHasVerificationError(false);
    setErrorMsg("");
    setInfoMsg("");
    resetTurnstile();
  }, [resetTurnstile]);

  return {
    step,
    email,
    setEmail,
    codeValue,
    isSending,
    isVerifying,
    hasVerificationError,
    canSubmitEmail,
    canResend,
    resendSeconds,
    turnstileSiteKey,
    turnstileResetKey,
    setTurnstileToken,
    errorMsg,
    infoMsg,
    requestLogin,
    verifyCode,
    handleCodeChange,
    goBackToEmail,
    reset,
  };
}
